import { Breakpoints } from '@angular/cdk/layout';
import { Component, Inject } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, NEVER, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableComponent,
  makeQueryBuilder,
  makeQueryRules,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { SwalService } from '@atmi/core';
import { Term, Vocabulary } from '@atmi/core/models';
import { VocabularyService } from '@atmi/core/services/vocabulary.service';

export interface VocabularyDialogData {
  vocabulary: Vocabulary;
}

/**
 * TODO: Refactor apply filters management in more reusable way without duplicate code.
 */
@Component({
  selector: 'app-vocabulary-advanced-manager-dialog',
  styleUrls: ['./vocabulary-advanced-manager-dialog.component.scss'],
  templateUrl: './vocabulary-advanced-manager-dialog.component.html',
})
export class VocabularyAdvancedManagerDialogComponent extends BaseTableComponent<Term> implements UiTableComponent<Term> {
  selectedTerms: Term[] = [];

  readonly selectedVocabulary = this.data.vocabulary;

  readonly fields: TableField<Term>[] = [
    {
      header: this.translate.instant('Valore'),
      key: 'valore',
    },
    {
      header: this.translate.instant('Lingua'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'lingua',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'createdAt',
      template: 'date-time',
    },
  ];

  readonly search$ = new BehaviorSubject<string | undefined>(undefined);

  readonly pager$ = of((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    return this.vocabularyApi.getTerms({ page, limit, sort, filter });
  });

  readonly contextMenus: Array<TableContextMenu<Term>> = [
    {
      action: (item) => this.selectTerm(item),
      label: this.translate.instant('Seleziona'),
    },
  ];

  readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  constructor(
    private readonly vocabularyApi: VocabularyService,
    private readonly dialogRef: MatDialogRef<VocabularyAdvancedManagerDialogComponent>,
    private readonly roleService: RoleService,
    @Inject(MAT_DIALOG_DATA) public readonly data: VocabularyDialogData,
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
  ) {
    super(translate, router, route, swalService);

    if (this.roleService.hasCapability('Termine', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id, item) : undefined,
        label: this.translate.instant('Rimuovi'),
      });
    }
    this.searchTermHandler(undefined); // Use to init filters data
  }

  addTermHandler(value: string, vocabulary: Vocabulary) {
    const lastTermSelected = this.getLastSelectedTerm();

    const newTerm: Term = {
      lingua: 'it_IT', // TODO: Remove embedded
      termineGenitore: lastTermSelected && lastTermSelected.id,
      valore: value,
      vocabolario: vocabulary.id,
    };
    this.vocabularyApi.postTerm(newTerm).subscribe(() => {
      this.reload$.next(true);
    });
  }

  // tslint:disable-next-line:variable-name
  deleteConcreteHandler(id: string, _item: Term) {
    return this.vocabularyApi.deleteTerm(id).pipe(
      tap(() => this.reload$.next(true)),
    );
  }

  deleteSelectedConcreteHandler() {
    return NEVER;
  }

  // tslint:disable-next-line:variable-name
  selectTermBreadcrumbHandler(_term: Term, index: number) {
    this.selectedTerms = this.selectedTerms.filter((_, i) => i <= index);

    const lastTerm = this.getLastSelectedTerm();
    const rules = makeQueryRules(
      { valore: undefined, vocabolario: this.selectedVocabulary.id, termineGenitore: lastTerm ? lastTerm.id : 'null' },
      {
        termineGenitore: { operator: 'equal', typeField: 'oid' },
        valore: { operator: 'contains' },
        vocabolario: { operator: 'equal', typeField: 'oid' },
      },
    );
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  searchTermHandler(search?: string) {
    const lastTerm = this.getLastSelectedTerm();

    const rules = makeQueryRules(
      { valore: search, vocabolario: this.selectedVocabulary.id, termineGenitore: lastTerm ? lastTerm.id : 'null' },
      {
        termineGenitore: { operator: 'equal', typeField: 'oid' },
        valore: { operator: 'contains' },
        vocabolario: { operator: 'equal', typeField: 'oid' },
      },
    );
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  removeAllSelectedTermsHandler() {
    this.selectedTerms = [];

    const rules = makeQueryRules(
      { valore: undefined, vocabolario: this.selectedVocabulary.id, termineGenitore: 'null' },
      {
        termineGenitore: { operator: 'equal', typeField: 'oid' },
        valore: { operator: 'contains' },
        vocabolario: { operator: 'equal', typeField: 'oid' },
      },
    );
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

  getLastSelectedTerm() {
    const lastTerm = this.selectedTerms.length > 0
      ? this.selectedTerms[this.selectedTerms.length - 1]
      : undefined;

    return lastTerm;
  }

  private selectTerm(term: Term) {
    this.selectedTerms = [...this.selectedTerms, term];

    const rules = makeQueryRules(
      { valore: undefined, vocabolario: this.selectedVocabulary.id, termineGenitore: term.id },
      {
        termineGenitore: { operator: 'equal', typeField: 'oid' },
        valore: { operator: 'contains' },
        vocabolario: { operator: 'equal', typeField: 'oid' },
      },
    );
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }

}
